/**
 * Employee Types for HR Module
 */

export type TypeContrat = 'CDI' | 'CDD' | 'ANAPEC' | 'INTERIM' | 'ETRANGER' | 'STAGE';

export type StatutEmploye = 'ACTIF' | 'INACTIF' | 'SUSPENDU';

export type SituationFamiliale = 'CELIBATAIRE' | 'MARIE' | 'DIVORCE' | 'VEUF';

export type MotifSortie = 'DEMISSION' | 'LICENCIEMENT' | 'FIN_CDD' | 'RETRAITE' | 'AUTRE';

export interface Employee {
  id: number;
  matricule: string;
  nom_complet: string;
  date_naissance: string | null;
  lieu_naissance: string | null;
  nationalite: string | null;
  cin: string | null;
  adresse: string | null;
  telephone: string | null;
  situation_familiale: SituationFamiliale;
  nbr_enfants: number;
  nom_complet_ar?: string | null;
  lieu_naissance_ar?: string | null;
  adresse_ar?: string | null;
  photo_url: string | null;
  date_embauche: string;
  fonction: string | null;
  statut: StatutEmploye;
  anciennete: string;
  type_contrat: TypeContrat;
  duree_contrat_mois: number | null;
  date_fin_contrat: string | null;
  relance_contrat: boolean;
  date_sortie: string | null;
  motif_sortie: MotifSortie | null;
  numero_cnss: string | null;
  banque: string | null;
  rib: string | null;
  salaire_base: number | null;
  salaire_net: number | null;
  declaration_journaliere: number | null;
  // Payroll calculated fields
  salaire_brut_global: number | null;
  salaire_brut_imposable: number | null;
  total_cotisations: number | null;
  frais_professionnel: number | null;
  salaire_net_imposable: number | null;
  ir_brut: number | null;
  deduction_charge_famille: number | null;
  ir_net: number | null;
  created_at: string;
  updated_at: string;
}

export interface EmployeeCreate {
  nom_complet: string;
  date_naissance?: string | null;
  lieu_naissance?: string | null;
  nationalite?: string | null;
  cin?: string | null;
  adresse?: string | null;
  telephone?: string | null;
  situation_familiale?: SituationFamiliale;
  nbr_enfants?: number;
  nom_complet_ar?: string | null;
  lieu_naissance_ar?: string | null;
  adresse_ar?: string | null;
  photo_url?: string | null;
  date_embauche: string;
  fonction?: string | null;
  statut?: StatutEmploye;
  type_contrat?: TypeContrat;
  duree_contrat_mois?: number | null;
  date_fin_contrat?: string | null;
  relance_contrat?: boolean;
  date_sortie?: string | null;
  motif_sortie?: MotifSortie | null;
  numero_cnss?: string | null;
  banque?: string | null;
  rib?: string | null;
  salaire_base?: number | null;
  salaire_net?: number | null;
  declaration_journaliere?: number | null;
  // Payroll calculated fields
  salaire_brut_global?: number | null;
  salaire_brut_imposable?: number | null;
  total_cotisations?: number | null;
  frais_professionnel?: number | null;
  salaire_net_imposable?: number | null;
  ir_brut?: number | null;
  deduction_charge_famille?: number | null;
  ir_net?: number | null;
}

export interface EmployeeUpdate extends Partial<EmployeeCreate> {}

export interface EmployeeListResponse {
  items: Employee[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface EmployeeStats {
  total_employees: number;
  active_employees: number;
  inactive_employees: number;
  by_contract_type: Record<string, number>;
  by_situation_familiale: Record<string, number>;
}

export interface EmployeeDropdownItem {
  id: number;
  matricule: string;
  nom_complet: string;
  fonction: string | null;
  type_contrat: TypeContrat;
}

// Constants for dropdowns
export const TYPE_CONTRAT_OPTIONS: { value: TypeContrat; label: string }[] = [
  { value: 'CDI', label: 'CDI - Contrat à Durée Indéterminée' },
  { value: 'CDD', label: 'CDD - Contrat à Durée Déterminée' },
  { value: 'ANAPEC', label: 'ANAPEC - Contrat d\'Insertion' },
  { value: 'INTERIM', label: 'Intérim - Travail Temporaire' },
  { value: 'ETRANGER', label: 'Étranger - Contrat Travailleur Étranger' },
  { value: 'STAGE', label: 'Stage - Contrat de Stage' },
];

export const STATUT_EMPLOYE_OPTIONS: { value: StatutEmploye; label: string }[] = [
  { value: 'ACTIF', label: 'Actif' },
  { value: 'INACTIF', label: 'Inactif' },
  { value: 'SUSPENDU', label: 'Suspendu' },
];

export const MOTIF_SORTIE_OPTIONS: { value: MotifSortie; label: string }[] = [
  { value: 'DEMISSION', label: 'Démission' },
  { value: 'LICENCIEMENT', label: 'Licenciement' },
  { value: 'FIN_CDD', label: 'Fin de CDD' },
  { value: 'RETRAITE', label: 'Retraite' },
  { value: 'AUTRE', label: 'Autre' },
];

export const SITUATION_FAMILIALE_OPTIONS: { value: SituationFamiliale; label: string }[] = [
  { value: 'CELIBATAIRE', label: 'Célibataire' },
  { value: 'MARIE', label: 'Marié(e)' },
  { value: 'DIVORCE', label: 'Divorcé(e)' },
  { value: 'VEUF', label: 'Veuf(ve)' },
];

// Employee Document Types
export type DocumentType = 'CONTRAT' | 'CIN' | 'DIPLOME' | 'CV' | 'RIB' | 'CNSS' | 'AUTRE';

export interface EmployeeDocument {
  id: number;
  employee_id: number;
  document_type: DocumentType;
  title: string;
  description: string | null;
  file_url: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface EmployeeDocumentListResponse {
  documents: EmployeeDocument[];
  total: number;
}

export const DOCUMENT_TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: 'CONTRAT', label: 'Contrat de travail' },
  { value: 'CIN', label: 'Copie CIN' },
  { value: 'DIPLOME', label: 'Diplôme / Certificat' },
  { value: 'CV', label: 'CV' },
  { value: 'RIB', label: 'RIB Bancaire' },
  { value: 'CNSS', label: 'Attestation CNSS' },
  { value: 'AUTRE', label: 'Autre document' },
];
